import { createLimsClient, LimsApiClient } from '@mariana/labos-client';
import type { EntityType, Attribute } from '@mariana/labos-client';

const runtime = typeof window !== 'undefined' ? window.__LIMS_CONFIG__ : undefined;
const API_BASE_URL =
  runtime?.baseUrl ??
  process.env.REACT_APP_LABOS_SERVER_URL ??
  process.env.LABOS_SERVER_URL ??
  '';

const _client = createLimsClient({
  baseUrl: API_BASE_URL,
});

const attrCache = new Map<EntityType, { data: Attribute[]; ts: number }>();
const CACHE_TTL_MS = 5 * 60 * 1000;
const inflightRequests = new Map<EntityType, Promise<Attribute[]>>();

/**
 * Cached wrapper around getAttributes. Attributes rarely change within a
 * session, but multiple components (ColumnPicker, EntityFilters, EntityDetail,
 * resolveSourceColumns) all call this independently.
 */
function getCachedAttributes(entityType: EntityType): Promise<Attribute[]> {
  const cached = attrCache.get(entityType);
  if (cached && Date.now() - cached.ts < CACHE_TTL_MS) {
    return Promise.resolve(cached.data);
  }

  const inflight = inflightRequests.get(entityType);
  if (inflight) return inflight;

  const request = _client.getAttributes(entityType).then(data => {
    attrCache.set(entityType, { data, ts: Date.now() });
    inflightRequests.delete(entityType);
    return data;
  }).catch(err => {
    inflightRequests.delete(entityType);
    throw err;
  });

  inflightRequests.set(entityType, request);
  return request;
}

export const limsClient: LimsApiClient = new Proxy(_client, {
  get(target, prop, receiver) {
    if (prop === 'getAttributes') return getCachedAttributes;
    return Reflect.get(target, prop, receiver);
  },
});

export type { LimsApiClient };
